import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import GiftCard from "@/components/GiftCard";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RefreshCw, Wallet, BookmarkPlus, Search, Info, Loader2 } from "lucide-react";

const REFINE_REASONS = [
  { v: "too_boring", l: "Too boring" },
  { v: "too_expensive", l: "Too expensive" },
  { v: "too_generic", l: "Too generic" },
  { v: "already_owns", l: "Already owns similar" },
  { v: "not_their_style", l: "Not their style" },
  { v: "prefer_experience", l: "Prefer an experience" },
  { v: "prefer_personalised", l: "Prefer personalised" },
  { v: "more_unusual", l: "More unusual" },
];

const BUDGETS = [
  { v: "u25", l: "Under £25" }, { v: "25_50", l: "£25 – £50" }, { v: "50_100", l: "£50 – £100" },
  { v: "100_250", l: "£100 – £250" }, { v: "250p", l: "£250+" },
];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function Results() {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [loading, setLoading] = useState(false);

  const [refineOpen, setRefineOpen] = useState(false);
  const [refineReasons, setRefineReasons] = useState([]);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budgetSel, setBudgetSel] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [recipName, setRecipName] = useState("");
  const [recipDate, setRecipDate] = useState("");
  const [recipReminder, setRecipReminder] = useState("7");

  useEffect(() => {
    const raw = sessionStorage.getItem("gm_result");
    if (!raw) { navigate("/quiz"); return; }
    const parsed = JSON.parse(raw);
    setData(parsed);
    setAnswers(parsed.answers);
    setRecipName(cap(parsed.answers?.recipient) || "");
    // eslint-disable-next-line
  }, []);

  const regenerate = async ({ reasons = [], addExclude = [] } = {}) => {
    setLoading(true);
    try {
      const exclude_ids = [...new Set([...excluded, ...addExclude])];
      const res = await api.post("/recommend", {
        answers,
        session_id: data.session_id,
        refine: { reasons, exclude_ids },
      });
      const merged = { ...res.data, answers };
      setData(merged);
      sessionStorage.setItem("gm_result", JSON.stringify(merged));
      setExcluded(exclude_ids);
    } catch {
      toast.error("Could not refine results");
    } finally {
      setLoading(false);
    }
  };

  const applyRefine = async () => {
    setRefineOpen(false);
    await regenerate({ reasons: refineReasons });
    setRefineReasons([]);
    toast.success("Refreshed your shortlist");
  };

  const applyBudget = async () => {
    if (!budgetSel) return;
    const updated = { ...answers, budget: { type: "preset", preset: budgetSel }, budget_preset: budgetSel, budget_label: BUDGETS.find((b) => b.v === budgetSel)?.l };
    setAnswers(updated);
    setBudgetOpen(false);
    setLoading(true);
    try {
      const res = await api.post("/recommend", { answers: updated, session_id: data.session_id, refine: { exclude_ids: excluded } });
      const merged = { ...res.data, answers: updated };
      setData(merged);
      sessionStorage.setItem("gm_result", JSON.stringify(merged));
      toast.success("Budget updated");
    } catch { toast.error("Could not update budget"); }
    finally { setLoading(false); }
  };

  const markUnsuitable = async (pid) => {
    await regenerate({ addExclude: [pid] });
    toast("Swapped that one out", { icon: "👍" });
  };

  const saveRecipient = async () => {
    if (!user) { setSaveOpen(false); setAuthOpen(true); return; }
    try {
      await api.post("/recipients", {
        name: recipName || "Recipient", answers,
        occasion_date: recipDate || null,
        reminder_days: parseInt(recipReminder) || 7,
      });
      toast.success(recipDate ? "Recipient saved — we'll remind you by email" : "Recipient saved");
      setSaveOpen(false);
    } catch { toast.error("Could not save"); }
  };

  if (!data) return null;

  const recs = data.recommendations || [];
  const relationship = cap(answers?.recipient) || "them";

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-10 py-8 lg:py-12 gm-fade">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
        <div className="gm-fade-up">
          <span className="font-mono text-xs uppercase tracking-widest text-brand-amber font-semibold">Your curated shortlist</span>
          <h1 className="font-serif text-3xl lg:text-5xl mt-2 text-brand-ink">
            We found some gifts for {relationship}
          </h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            {recs.length} handpicked ideas for {answers?.occasion?.replace(/_/g, " ")} within your budget{answers?.budget_label ? ` (${answers.budget_label})` : ""}. Every pick is explained below.
          </p>
        </div>
      </div>

      {data.relaxed && (
        <div className="flex items-start gap-3 bg-brand-amber/10 border border-brand-amber/30 rounded-xl p-4 mb-6 text-sm">
          <Info className="w-4 h-4 text-brand-amber mt-0.5 shrink-0" />
          <p>We widened your budget a little to show a fuller shortlist. Use <b>Change budget</b> to adjust.</p>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-3 mb-8">
        <Button onClick={() => setRefineOpen(true)} disabled={loading} data-testid="refine-results-button"
          className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Not quite right — try again
        </Button>
        <Button onClick={() => setBudgetOpen(true)} variant="outline" data-testid="change-budget-button"
          className="rounded-full gap-2 border-brand-green/30 hover:bg-brand-cream2">
          <Wallet className="w-4 h-4" /> Change budget
        </Button>
        <Button onClick={() => setSaveOpen(true)} variant="outline" data-testid="save-recipient-button"
          className="rounded-full gap-2 border-brand-green/30 hover:bg-brand-cream2">
          <BookmarkPlus className="w-4 h-4" /> Save recipient
        </Button>
        <Button onClick={() => navigate("/quiz")} variant="ghost" data-testid="new-search-button"
          className="rounded-full gap-2 hover:bg-brand-cream2">
          <Search className="w-4 h-4" /> New search
        </Button>
      </div>

      <p className="text-xs text-muted-foreground mb-6" data-testid="affiliate-disclosure">
        Choicefully may earn a commission when you purchase through links on our site, at no additional cost to you.
      </p>

      {recs.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-brand-cream2">
          <p className="font-serif text-2xl mb-2">No matches just yet</p>
          <p className="text-muted-foreground mb-6">Try widening your budget or preferences.</p>
          <Button onClick={() => setBudgetOpen(true)} className="bg-brand-green hover:bg-brand-greendark rounded-full">Change budget</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {recs.map((r, i) => (
            <GiftCard key={r.product.product_id} rec={r} index={i} sessionId={data.session_id}
              recId={data.rec_id} onUnsuitable={markUnsuitable} />
          ))}
        </div>
      )}

      {/* Refine dialog */}
      <Dialog open={refineOpen} onOpenChange={setRefineOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="refine-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">What wasn't quite right?</DialogTitle>
            <DialogDescription>Pick any that apply and we'll retune the shortlist.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2.5 py-2">
            {REFINE_REASONS.map((r) => {
              const active = refineReasons.includes(r.v);
              return (
                <button key={r.v} data-testid={`refine-reason-${r.v}`}
                  onClick={() => setRefineReasons((p) => active ? p.filter((x) => x !== r.v) : [...p, r.v])}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${active ? "border-brand-green bg-brand-green text-brand-cream" : "border-brand-cream2 hover:bg-brand-cream2"}`}>
                  {r.l}
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={applyRefine} disabled={loading} data-testid="refine-apply-button"
              className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Show new picks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget dialog */}
      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent className="bg-white" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Change budget</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {BUDGETS.map((b) => (
              <button key={b.v} data-testid={`budget-dialog-${b.v}`} onClick={() => setBudgetSel(b.v)}
                className={`h-14 rounded-xl border font-mono text-sm font-semibold transition-all ${budgetSel === b.v ? "border-brand-green bg-brand-green text-brand-cream" : "border-brand-cream2 hover:border-brand-green/40"}`}>
                {b.l}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={applyBudget} disabled={!budgetSel || loading} className="bg-brand-green hover:bg-brand-greendark rounded-full">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save recipient dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-white" data-testid="save-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Save this recipient</DialogTitle>
            <DialogDescription>
              {user ? "We'll keep their details so you can re-run this search later." : "Sign in to save recipients and revisit your searches."}
            </DialogDescription>
          </DialogHeader>
          {user && (
            <div className="py-2 space-y-4">
              <div>
                <Label htmlFor="recip-name">Name / label</Label>
                <Input id="recip-name" data-testid="save-recipient-name-input" value={recipName}
                  onChange={(e) => setRecipName(e.target.value)} placeholder="e.g. Mum — Birthday in Nov" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="recip-date">Occasion date (optional)</Label>
                  <Input id="recip-date" type="date" data-testid="save-recipient-date-input" value={recipDate}
                    onChange={(e) => setRecipDate(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="recip-remind">Remind me</Label>
                  <select id="recip-remind" data-testid="save-recipient-reminder-select" value={recipReminder}
                    onChange={(e) => setRecipReminder(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                    <option value="3">3 days before</option>
                    <option value="7">1 week before</option>
                    <option value="14">2 weeks before</option>
                    <option value="30">1 month before</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">We'll email you a gentle reminder before the date.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveRecipient} data-testid="save-recipient-confirm-button"
              className="bg-brand-green hover:bg-brand-greendark rounded-full">
              {user ? "Save recipient" : "Sign in"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
